import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../components/primitives/Panel';
import { PostsPerDay } from './usePostsPerDay';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostsWentOutPanelProps {
  perDay: PostsPerDay;
  windowDays: number;
}

/**
 * What went out, day by day.
 *
 * The design puts reach here. No platform reports reach to us, so this counts
 * the one thing we hold ourselves: the posts that were published.
 */
const PostsWentOutPanelComponent: React.FC<PostsWentOutPanelProps> = ({ perDay, windowDays }) => {
  const intl = useIntl();

  const bars = useMemo(
    () =>
      perDay.days.map((day) => {
        const height = perDay.peak === 0 ? 0 : Math.round((day.count / perDay.peak) * 100);
        const label = intl.formatDate(day.at, { dateStyle: 'medium' });
        return (
          <div key={day.key} className={styles.barSlot} title={`${label} · ${intl.formatNumber(day.count)}`}>
            <div
              className={clsx(styles.bar, day.count === 0 && styles.barEmpty)}
              style={{ height: `${Math.max(height, day.count === 0 ? 0 : 6)}%` }}
            />
          </div>
        );
      }),
    [intl, perDay.days, perDay.peak]
  );

  const first = perDay.days[0];
  const last = perDay.days[perDay.days.length - 1];

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faPaperPlane} />}
        title={intl.formatMessage({ id: 'app.sml.promote.performance.wentOut', defaultMessage: 'Posts that went out' })}
        right={
          <span className={styles.totalTag}>
            {intl.formatMessage(
              { id: 'app.sml.promote.performance.window', defaultMessage: 'LAST {days} DAYS' },
              { days: windowDays }
            )}
          </span>
        }
      />
      <PanelBody>
        <div className={styles.total}>
          <span className={styles.wentOutTotal}>{intl.formatNumber(perDay.total)}</span>
          <span className={styles.totalTag}>
            {intl.formatMessage({ id: 'app.sml.promote.performance.published', defaultMessage: 'posts published' })}
          </span>
        </div>
        <div className={styles.bars}>{bars}</div>
        <div className={styles.axis}>
          <span>{first === undefined ? '' : intl.formatDate(first.at, { month: 'short', day: 'numeric' })}</span>
          <span>{last === undefined ? '' : intl.formatDate(last.at, { month: 'short', day: 'numeric' })}</span>
        </div>
        <p className={styles.heroNote}>
          {intl.formatMessage({
            id: 'app.sml.promote.performance.wentOut.note',
            defaultMessage: 'One bar per day, counted from the posts you confirmed as published.',
          })}
        </p>
      </PanelBody>
    </Panel>
  );
};

export const PostsWentOutPanel = TypedMemo(PostsWentOutPanelComponent);
